'use client';

import { ReactNode } from 'react';
import { usePathname } from 'next/navigation';
import Sidebar from '../../../core/layout/Sidebar';
import ContentWindow from '../../../core/layout/ContentWindow';
import { TourProvider, TOUR_REGISTRY } from '@/features/tour';
import { AdminTourAutoStart, AdminTourOverlay } from '@/features/tour/AdminTour';
import { TemplatesTourOverlay } from '@/features/tour/TemplatesTour';
import { AssignmentsTourOverlay } from '@/features/tour/AssignmentsTour';
import { TagsTourOverlay } from '@/features/tour/TagsTour';
import { RegistrationTourOverlay } from '@/features/tour/RegistrationTour';
import { EoGuardrailsTourOverlay } from '@/features/tour/EoGuardrailsTour';
import { LoggingTourOverlay } from '@/features/tour/LoggingTour';
import { WhyVoleyTourOverlay } from '@/features/tour/WhyVoleyTour';
import { WhySmsTourOverlay } from '@/features/tour/WhySmsTour';
import { ContactManagementTourOverlay } from '@/features/tour/ContactManagementTour';
import { ConversationMgmtTourOverlay } from '@/features/tour/ConversationMgmtTour';
import { CrmConnectTourOverlay } from '@/features/tour/CrmConnectTour';
import { AmsConnectTourOverlay } from '@/features/tour/AmsConnectTour';

interface DashboardShellProps {
  children: ReactNode;
}

/**
 * Dashboard Shell - Smooth navigation with quick fade
 *
 * Uses CSS fade-in animation on route change (key={pathname} forces remount).
 * - No exit animation (prevents content->gap->content flash)
 * - Quick 200ms CSS fade-in for smoothness
 * - NProgress bar provides immediate click feedback
 */
export default function DashboardShell({ children }: DashboardShellProps) {
  const pathname = usePathname();

  return (
    <TourProvider tours={TOUR_REGISTRY}>
      <AdminTourAutoStart />
      <div className="dashboard-layout">
        {/* Tour overlays render on top of everything when active */}
        <WhyVoleyTourOverlay />
        <AdminTourOverlay />
        <TemplatesTourOverlay />
        <AssignmentsTourOverlay />
        <TagsTourOverlay />
        <RegistrationTourOverlay />
        <EoGuardrailsTourOverlay />
        <LoggingTourOverlay />
        <ContactManagementTourOverlay />
        <ConversationMgmtTourOverlay />
        <WhySmsTourOverlay />
        <CrmConnectTourOverlay />
        <AmsConnectTourOverlay />

        {/* Sidebar renders immediately */}
        <Sidebar />

        {/* Main content area */}
        <main className="dashboard-main">
          {/* Content */}
          <div className="dashboard-content-wrapper pr-6 pl-0 py-5">
            {/* Use flex-1 and min-h-0 instead of h-full to properly account for parent padding */}
            <div className="relative w-full flex-1 min-h-0 flex flex-col">
              <ContentWindow fullHeight className="w-full flex-1" height="h-full">
                <div key={pathname} className="h-full animate-fade-in">
                  {children}
                </div>
              </ContentWindow>
            </div>
          </div>
        </main>
      </div>
    </TourProvider>
  );
}
